/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

// The one footer for the whole site. Self-contained (MUI only, no Bootstrap grid
// or uncommitted theme CSS that the legacy footer-light.js needed), dark to match
// every page, and a superset of both prior footers: every destination the legacy
// footer exposed is here, including Delete User (an app-store account-deletion
// requirement), Our Partners, the developer docs, and the full social set.
// Redundant duplicates that all pointed at /contact (Accounting, Billing, For
// Marketing, For Agencies, General Question) collapse to one Contact link, so no
// destination is lost.
//
// The year is a constant, not new Date().getFullYear(): clock-derived text is
// captured at prerender time and would mismatch on hydration in a later year
// (React #425). Bump it at the yearly release.
//
// NOTE: "Career" still points at etime.hertzai.com/jobs, the last eTime leftover,
// kept to preserve the legacy nav until a hevolve.ai target is chosen.
const YEAR = 2026;

const COLUMNS = [
  {
    heading: 'Explore',
    links: [
      { label: 'Home', to: '/' },
      { label: 'Hevolve Agents', to: '/agents' },
      { label: 'Answers', to: '/answers' },
      { label: 'Research', to: '/research' },
      { label: 'News', to: '/news' },
      { label: 'Incidents', to: '/incidents' },
      { label: 'Hive census', to: '/hive' },
      { label: 'Listings', to: '/listings' },
      { label: 'Blog', to: '/blog' },
      { label: 'Download Nunba', to: '/download' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About Hevolve', to: '/aboutus' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Press & Media', to: '/press' },
      { label: 'Our Partners', to: '/partners' },
      { label: 'Career', href: 'https://etime.hertzai.com/jobs', external: true },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    heading: 'Developers',
    links: [
      { label: 'Developer Docs', to: '/docs' },
      { label: 'Mindstory SDK', to: '/docs/sdk' },
      { label: 'Hive API', to: '/docs/hive' },
      { label: 'Earn Spark', to: '/earn' },
      { label: 'Pupit', to: '/pupit' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/legal/privacy' },
      { label: 'Delete User', to: '/deleteUser' },
    ],
  },
];

const SOCIAL = [
  { label: 'GitHub', href: 'https://github.com/hertz-ai/Nunba' },
  { label: 'X', href: 'https://twitter.com/AiHertz' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/hertz-ai/' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UClzFvo8SECdyd0dVQhJ2Cbg' },
];

const linkSx = {
  color: 'rgba(255,255,255,0.62)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: 'color 0.2s',
  '&:hover': { color: '#fff' },
};

function FooterLink({ item }) {
  if (item.external) {
    return (
      <Box component="a" href={item.href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
        {item.label}
      </Box>
    );
  }
  return (
    <Box component={Link} to={item.to} sx={linkSx}>
      {item.label}
    </Box>
  );
}

export default function SeoFooter() {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#0B0A12',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        color: '#fff',
        py: { xs: 5, md: 7 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand column */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Hevolve AI
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 320, lineHeight: 1.7 }}>
              Local-first, self-evolving multimodal AI agents. Build them by talking, run them
              on your own machine.
            </Typography>
          </Grid>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <Grid item xs={6} sm={3} md={2} key={col.heading}>
              <Typography
                variant="subtitle2"
                sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}
              >
                {col.heading}
              </Typography>
              <Stack spacing={1}>
                {col.links.map((l) => (
                  <FooterLink item={l} key={l.label} />
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            pt: 3,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { sm: 'center' },
            gap: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
            {YEAR} © HertzAI Pvt Ltd. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            {SOCIAL.map((s) => (
              <Box component="a" href={s.href} target="_blank" rel="noopener noreferrer" sx={linkSx} key={s.label}>
                {s.label}
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
